import React from 'react';

import { FormattedMessage, type IntlShape, useIntl } from 'react-intl';
import { graphql, useFragment } from 'react-relay';

import { fg } from '@atlaskit/platform-feature-flags';

import type { ThirdPartyConfigsBootstrap } from '../../../../../../../../common/constants/schemas/3p-config';
import { WORKSPACE_NAME_MAX_LENGTH } from '../../../../../../../../common/ui/workspace-name-metadata';
import { serveFederatedSlackExperiment } from '../../../../../../../../common/utils/experiments/experiment-utils';

import type { threadTitle_AtlassianSearchPage$key } from './__generated__/threadTitle_AtlassianSearchPage.graphql';
import { i18n } from './messages';

const USER_DISPLAY_LIMIT = 3;
interface ThreadTitleProps {
	node: threadTitle_AtlassianSearchPage$key;
	thirdPartyConfigs?: ThirdPartyConfigsBootstrap;
}

type ThirdPartyUser = {
	id: string;
	name?: string | null;
};

const dedupUsers = (
	userMap: Record<string, { thirdPartyUser: ThirdPartyUser; index: number }>,
	user: ThirdPartyUser,
	index: number,
) => {
	if (!userMap[user.id]) {
		userMap[user.id] = { thirdPartyUser: user, index };
	}
	return userMap;
};

const getTitleMessageValues = (
	intl: IntlShape,
	users: (ThirdPartyUser | undefined | null)[] = [],
) => {
	const { formatMessage } = intl;
	const uniqueUsers = users
		.filter((thirdPartyUser): thirdPartyUser is ThirdPartyUser =>
			Boolean(thirdPartyUser?.name?.trim()),
		)
		.reduce(dedupUsers, {});
	const validContributors = Object.values(uniqueUsers)
		.sort((a, b) => a.index - b.index)
		.map((entry) => entry?.thirdPartyUser?.name || formatMessage(i18n.externalPersonLabel))
		.map((name) => name?.split(' ')[0]);

	const externalPersonLabel = formatMessage(i18n.externalPersonLabel);
	const externalPersonCount = validContributors.filter(
		(name) => name === externalPersonLabel,
	).length;

	let adjustedContributors = validContributors;
	if (externalPersonCount > 1) {
		adjustedContributors = validContributors.filter((name) => name !== externalPersonLabel);
		adjustedContributors.unshift(formatMessage(i18n.externalPeopleLabel));
	}

	const contributorCount = adjustedContributors.length;
	const namedContributors = adjustedContributors.slice(0, USER_DISPLAY_LIMIT);
	const overflowContributorCount = adjustedContributors.slice(USER_DISPLAY_LIMIT).length;

	return { contributorCount, namedContributors, overflowContributorCount };
};

const truncateWorkspaceName = (name: string): string => {
	if (name.length <= WORKSPACE_NAME_MAX_LENGTH) {
		return name;
	}
	return `${name.slice(0, WORKSPACE_NAME_MAX_LENGTH)}...`;
};

const getRegularThreadTitleValues = (data: any, intl: IntlShape, formatMessage: any) => {
	const linkedEntities = data.linkedEntities || [];
	const threadUsers = [data, ...linkedEntities].map(
		(linkedMessage) =>
			linkedMessage?.entity?.createdBy?.thirdPartyUser || linkedMessage?.initialContributors?.[0],
	);

	const { contributorCount, namedContributors, overflowContributorCount } = getTitleMessageValues(
		intl,
		threadUsers,
	);

	const [name1, name2, name3] = namedContributors;
	const rawWorkspaceName = data?.workspaceName;
	const channelName =
		data?.entity?.container?.displayName ||
		(rawWorkspaceName ? truncateWorkspaceName(rawWorkspaceName) : null) ||
		data?.containerName ||
		formatMessage(i18n.unknownChannel);

	return {
		values: {
			contributorCount,
			name1,
			name2,
			name3,
			overflowContributorCount,
			channelName: channelName,
		},
		titleMessage: i18n.threadTitleWithOthers,
	};
};

export const ThreadTitle = ({ node, thirdPartyConfigs }: ThreadTitleProps): React.JSX.Element => {
	const intl = useIntl();
	const { formatMessage } = intl;
	let values = {};
	let titleMessage = i18n.groupChatTitle;

	const data = useFragment(
		graphql`
			fragment threadTitle_AtlassianSearchPage on SearchResultGraphDocument {
				containerName @optIn(to: "SearchPerformanceImprovementChanges")
				workspaceName @optIn(to: "SearchPerformanceImprovementChanges")
				integrationId
				initialContributors {
					id
					name
				}
				entity {
					... on ExternalMessage {
						createdBy {
							thirdPartyUser {
								id
								name
							}
						}
						container @optIn(to: "ExternalDataDepotQueryV2") {
							... on ExternalConversation {
								displayName
								members {
									thirdPartyUser {
										id
										name
									}
								}
								type
							}
						}
					}
				}
				linkedEntities {
					initialContributors {
						id
						name
					}
					entity {
						... on ExternalMessage {
							createdBy {
								thirdPartyUser {
									id
									name
								}
							}
							container @optIn(to: "ExternalDataDepotQueryV2") {
								... on ExternalConversation {
									members {
										thirdPartyUser {
											id
											name
										}
									}
									type
								}
							}
						}
					}
				}
			}
		`,
		node,
	);

	const isSlackFederatedSearchEnabled = serveFederatedSlackExperiment(
		undefined,
		undefined,
		{ fireExperimentExposure: false },
		thirdPartyConfigs,
	);

	const isSlackIntegration = data.integrationId === 'ari:cloud:platform::integration/slack';

	if (data.containerName != null && isSlackFederatedSearchEnabled && isSlackIntegration) {
		const groupChatParticipants = data?.initialContributors;

		// direct message
		if (data.containerName === '') {
			const nonNullParticipants =
				groupChatParticipants?.filter((participant) => participant?.name) || [];
			const { namedContributors } = getTitleMessageValues(intl, nonNullParticipants);
			const [name1, name2] = namedContributors;

			if (nonNullParticipants.length === 1) {
				values = { name1 };
				titleMessage = i18n.directMessageSingleUser;
			} else {
				values = { name1, name2 };
				titleMessage = i18n.directMessageTwoUsers;
			}
		}
		// group direct message
		else if (data.containerName && /^mpdm-.+--.*-\d+$/.test(data.containerName)) {
			// Extract number of people from containerName (format: mpdm-name1--name2--name3-1)
			const totalPeopleCount = (data.containerName.match(/--/g) || []).length + 1;
			const nonNullParticipants =
				groupChatParticipants?.filter((participant) => participant?.name) || [];
			const otherCount = totalPeopleCount - nonNullParticipants.length;

			if (nonNullParticipants.length > 0) {
				const { namedContributors } = getTitleMessageValues(intl, nonNullParticipants);
				const displayedNames = namedContributors.slice(0, USER_DISPLAY_LIMIT);
				const [name1, name2, name3] = displayedNames;

				// Calculate total others (remaining known names + unknown people)
				const totalOthersCount = nonNullParticipants.length - displayedNames.length + otherCount;
				const hasOthers = totalOthersCount > 0;

				values = {
					knownNamesCount: nonNullParticipants.length,
					name1,
					name2,
					name3,
					totalOthersCount,
				};

				titleMessage = hasOthers
					? totalOthersCount === 1
						? i18n.federatedGroupDirectMessageWithOneOther
						: i18n.federatedGroupDirectMessageWithMultipleOthers
					: i18n.federatedGroupDirectMessage;
			} else {
				// Fallback if no names available
				values = { totalPeopleCount };
				titleMessage = i18n.groupDirectMessageUnknownNames;
			}
		}
		// fallback to regular thread title logic
		else {
			const result = getRegularThreadTitleValues(data, intl, formatMessage);
			values = result.values;
			titleMessage = result.titleMessage;
		}
	} else if (
		data?.entity?.container?.type === 'GROUP_MEETING' &&
		fg('rovo_fps_fix_unknown_channel_group_chat')
	) {
		const displayName = data?.entity?.container?.displayName?.trim();
		if (displayName) {
			// Named GROUP_MEETING — show "Bob in Create some random chat data" (no #)
			const result = getRegularThreadTitleValues(data, intl, formatMessage);
			values = { ...result.values, channelName: displayName };
			titleMessage = i18n.groupMeetingTitle;
		} else {
			// Unnamed GROUP_MEETING — show names only, don't show "unknown-channel"
			const groupChatParticipants =
				data?.entity?.container?.members?.map((member) => member?.thirdPartyUser) ?? [];
			const { contributorCount, namedContributors, overflowContributorCount } =
				getTitleMessageValues(intl, groupChatParticipants);
			const [name1, name2, name3] = namedContributors;
			values = { contributorCount, name1, name2, name3, overflowContributorCount };
			titleMessage = i18n.groupChatTitle;
		}
	} else if (
		data?.entity?.container?.type === 'DIRECT_MESSAGE' ||
		data?.entity?.container?.type === 'GROUP_DIRECT_MESSAGE'
	) {
		const groupChatParticipants =
			data?.entity?.container?.members?.map((member) => member?.thirdPartyUser) ?? [];

		const { contributorCount, namedContributors, overflowContributorCount } = getTitleMessageValues(
			intl,
			groupChatParticipants,
		);

		const [name1, name2, name3] = namedContributors;

		values = {
			contributorCount,
			name1,
			name2,
			name3,
			overflowContributorCount,
		};
		titleMessage = i18n.groupChatTitle;
	} else {
		const result = getRegularThreadTitleValues(data, intl, formatMessage);
		values = result.values;
		titleMessage = result.titleMessage;
	}

	return <FormattedMessage {...titleMessage} values={values} />;
};
